import type {
  Conversation,
  ConversationListPage,
  ConversationMessage,
  ConversationMessagesPage,
} from './types';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
}

export function unwrapData(response: unknown): unknown {
  const record = asRecord(response);
  if (record && 'data' in record) return record.data;
  return response;
}

function asNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asString(value: unknown): string {
  if (value == null) return '';
  return String(value);
}

function nullableText(value: unknown): string | null {
  const text = asString(value).trim();
  return text || null;
}

export function mapConversationMessage(value: unknown): ConversationMessage | null {
  const row = asRecord(value);
  if (!row || row.id == null) return null;
  return {
    id: asNumber(row.id),
    conversationId: asNumber(row.conversationId),
    senderId: asNumber(row.senderId),
    messageType: asString(row.messageType || 'text'),
    body: asString(row.body),
    createdAt: asString(row.createdAt),
    updatedAt: asString(row.updatedAt || row.createdAt),
  };
}

export function mapConversation(value: unknown): Conversation | null {
  const row = asRecord(value);
  if (!row || row.id == null) return null;
  return {
    id: asNumber(row.id),
    studentId: asNumber(row.studentId),
    mentorId: asNumber(row.mentorId),
    initiatedBy: asNumber(row.initiatedBy),
    createdBy: asNumber(row.createdBy),
    type: asString(row.type || 'direct'),
    status: asString(row.status || 'active'),
    lastMessageAt: row.lastMessageAt == null ? null : asString(row.lastMessageAt),
    unreadCount: asNumber(row.unreadCount),
    peerFirstName: nullableText(row.peerFirstName),
    peerLastName: nullableText(row.peerLastName),
    lastMessage: mapConversationMessage(row.lastMessage),
    createdAt: asString(row.createdAt),
    updatedAt: asString(row.updatedAt),
  };
}

export function mapConversationListPage(response: unknown): ConversationListPage {
  const payload = asRecord(unwrapData(response)) ?? {};
  const list = Array.isArray(payload.list) ? payload.list : [];
  return {
    list: list
      .map((row) => mapConversation(row))
      .filter((row): row is Conversation => row != null),
    page: asNumber(payload.page) || 1,
    limit: asNumber(payload.limit) || 20,
    total: asNumber(payload.total),
  };
}

export function mapConversationMessagesPage(
  response: unknown,
): ConversationMessagesPage {
  const payload = asRecord(unwrapData(response)) ?? {};
  const items = Array.isArray(payload.items) ? payload.items : [];
  return {
    items: items
      .map((row) => mapConversationMessage(row))
      .filter((row): row is ConversationMessage => row != null),
    nextCursor: payload.nextCursor == null ? null : asString(payload.nextCursor),
  };
}

export function mapConversationResponse(response: unknown): Conversation {
  const mapped = mapConversation(unwrapData(response));
  if (!mapped) {
    return {
      id: 0,
      studentId: 0,
      mentorId: 0,
      initiatedBy: 0,
      createdBy: 0,
      type: 'direct',
      status: 'active',
      lastMessageAt: null,
      unreadCount: 0,
      peerFirstName: null,
      peerLastName: null,
      lastMessage: null,
      createdAt: '',
      updatedAt: '',
    };
  }
  return mapped;
}
